import React, { useCallback, useEffect, useState } from 'react';
import { Col } from 'antd';

import {
  AssignmentStatusTypeEnum,
  CompanySelectModel,
  CustomerSearchResponseModel,
} from 'api/adminApiClient';
import { AssignmentFilterTypes } from '../enums';
import CompanyFilter from 'components/filters/CompanyFilter';
import {
  getAssignmentsCompanies,
  getCustomersByAssignments,
  getWorkersByAssignments,
} from 'redux/reducers/assignmentSlice';
import CustomerFilter from 'components/filters/CustomerFilter';
import WorkerFilter from 'components/filters/WorkerFilter';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  WorkerSearchResponseModel,
  WorkerTypeEnum,
} from 'api/backofficeApiClient';
import AssignmentTerminationReasonFilter from 'components/filters/AssignmentTerminationReasonFilter';
import AssignmentStatusFilter from 'components/filters/AssignmentStatusFilter';
import { DateParams } from 'utils/filterUtils';
import AssignmentDateFilter from 'components/filters/AssignmentDateFilter';
import WorkerTypeFilter from 'components/filters/WorkerTypeFilter';


export interface AssignmentFilters {
  [AssignmentFilterTypes.Company]?: string[];
  [AssignmentFilterTypes.Customer]?: string[];
  [AssignmentFilterTypes.Worker]?: string[];
  [AssignmentFilterTypes.WorkerType]?: WorkerTypeEnum[];
  [AssignmentFilterTypes.TerminationReason]?: number[];
  [AssignmentFilterTypes.Status]?: AssignmentStatusTypeEnum[];
  [AssignmentFilterTypes.Date]?: DateParams;
}

type Props = {
  filters: AssignmentFilters;
  assignmentStatuses: AssignmentStatusTypeEnum[];
  onChange: (data: AssignmentFilters) => void;
  isPendingSection?: boolean;
};

export default function AssignmentsFilter(props: Props) {
  const { filters, onChange, assignmentStatuses } = props;
  const isAdmin = useAppSelector(isAdminSelector);
  const [companies, setCompanies] = useState<CompanySelectModel[]>([]);
  const [isCompaniesLoaded, setIsCompaniesLoaded] = useState(false);
  const [customers, setCustomers] = useState<CustomerSearchResponseModel[]>([]);
  const [isCustomersLoaded, setIsCustomersLoaded] = useState(false);
  const [workers, setWorkers] = useState<WorkerSearchResponseModel[]>([]);
  const [isWorkersLoaded, setIsWorkersLoaded] = useState(false);

  const dispatch = useAppDispatch();

  const loadCompanies = useCallback(async () => {
    const result = await dispatch(getAssignmentsCompanies(assignmentStatuses));

    if (getAssignmentsCompanies.fulfilled.match(result)) {
      setCompanies(result.payload);
    }
    setIsCompaniesLoaded(true);
  }, [dispatch, assignmentStatuses]);

  const loadCustomers = useCallback(async () => {
    const result = await dispatch(
      getCustomersByAssignments({
        assignmentStatuses: assignmentStatuses,
        isAdminRole: isAdmin,
      }),
    );

    if (getCustomersByAssignments.fulfilled.match(result)) {
      setCustomers(result.payload);
    }
    setIsCustomersLoaded(true);
  }, [dispatch, isAdmin, assignmentStatuses]);

  const loadWorkers = useCallback(async () => {
    const result = await dispatch(getWorkersByAssignments(isAdmin as boolean));

    if (getWorkersByAssignments.fulfilled.match(result)) {
      setWorkers(result.payload);
    }
    setIsWorkersLoaded(true);
  }, [dispatch, isAdmin]);

  const loadData = useCallback(async () => {
    let requests = [];

    filters.hasOwnProperty(AssignmentFilterTypes.Company) &&
      requests.push(loadCompanies());
    filters.hasOwnProperty(AssignmentFilterTypes.Customer) &&
      requests.push(loadCustomers());
    filters.hasOwnProperty(AssignmentFilterTypes.Worker) &&
      requests.push(loadWorkers());

    await Promise.allSettled(requests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCompanies, loadCustomers, loadWorkers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <>
      {filters.hasOwnProperty(AssignmentFilterTypes.Company) && (
        <Col xs={24} md={6}>
          <CompanyFilter<AssignmentFilters>
            filters={filters}
            onChange={onChange}
            companies={companies}
            isCompaniesLoaded={isCompaniesLoaded}
            value={filters[AssignmentFilterTypes.Company]}
          />
        </Col>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.Customer) && (
        <Col xs={24} md={6}>
          <CustomerFilter
            filters={filters}
            customers={customers}
            onChange={onChange}
            isCustomersLoaded={isCustomersLoaded}
          />
        </Col>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.Worker) && (
        <Col xs={24} md={6}>
          <WorkerFilter
            filters={filters}
            workers={workers}
            onChange={onChange}
            isWorkersLoaded={isWorkersLoaded}
          />
        </Col>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.WorkerType) && (
        <Col xs={24} md={6}>
          <WorkerTypeFilter filters={filters} onChange={onChange} />
        </Col>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.Status) && (
        <Col xs={24} md={6}>
          <AssignmentStatusFilter filters={filters} onChange={onChange} />
        </Col>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.Date) && (
        <Col xs={24} md={6}>
          <AssignmentDateFilter filters={filters} onChange={onChange} />
        </Col>
      )}

      {filters.hasOwnProperty(AssignmentFilterTypes.TerminationReason) && (
        <Col xs={24} md={6}>
          <AssignmentTerminationReasonFilter
            filters={filters}
            onChange={onChange}
          />
        </Col>
      )}
    </>
  );
}
